import React, { useState } from "react";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import { teamList } from "../../../components/recoil/teamsRecoil";
import FormService from "../../../components/generic/formbuilder/networkservice/FormService";
import ConfigurationConstants from "../../utils/configurationconstants";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { performPostRequest } from "../../../service/network/NetworkService";
import { getApiUrl } from "../../../service/network/UrlMapper";
import { formSummaryDataAtom } from "../../../components/recoil/formSummaryRecoil";
import {
  dateAndTime,
  getDateString,
  getTimeSpentFromTimestamps,
  getTimeString,
} from "../../../utils/dateutils";
import { getFullName } from "../../careprovider/visits/utils";

const STATIC_HEADERS = [
  "Task Date",
  "Start Time",
  "End Time",
  "Time Spent",
  "Status",
  "Workforce",
  "Coordinator",
  "Patient",
  "MRN",
  "Submitted On",
];

const CsvExportButton = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const userDetails = useReadMemory(MemoryType.USER);
  const configuration = useRecoilValue(currentConfiguration);
  const teams = useRecoilValue(teamList);
  const formSummaryData = useRecoilValue(formSummaryDataAtom);
  const [loading, setLoading] = useState(false);
  const formService = new FormService();
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];
  const formBucketName =
    configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];
  const formCache = new Map();

  const getFormJson = async (templateCode) => {
    if (formCache.has(templateCode)) {
      return formCache.get(templateCode);
    }

    const promise = (async () => {
      try {
        const { data } = await formService.getContentId(templateCode);

        if (data && formBucketName?.value) {
          const formJson = await formService.getJsonFromObjectStore(
            data.contentId,
            formBucketName.value
          );
          return {
            formJson,
            formName: data.title,
          };
        }
        return null;
      } catch (err) {
        console.error(err);
        return null;
      }
    })();

    formCache.set(templateCode, promise);

    return promise;
  };

  const getFormValues = async (formIdentifier, templateCode) => {
    const payload = {
      intent: IntentType.GET_FORMDATA,
      payload: {
        formIdentifier,
        templateCode,
        secretKey: secretKey?.key,
      },
      context: {
        teamIds: teams.map((team) => team?.id),
        organizationIdentifier,
        userIdentifier: userDetails?.userIdentifier,
        role: userDetails?.jobTitle,
      },
    };
    try {
      const result = await performPostRequest(
        getApiUrl()[IntentType.GET_FORMDATA],
        JSON.stringify(payload)
      );
      const resultJson = await result.json();

      if (resultJson[0]?.formData) {
        return JSON.parse(resultJson[0]?.formData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const safeSheetName = (name) =>
    name.replace(/[:\\/?*\[\]]/g, "").slice(0, 30);

  const exportExcel = async (data) => {
    if (!data?.length) return;

    const grouped = {};

    // Group reports by templateCode
    for (const report of data) {
      const [formItem] = JSON.parse(report.formIdentifier);

      const [formTemplateData, formValues] = await Promise.all([
        getFormJson(formItem.templateCode),
        getFormValues(formItem.formIdentifier, formItem.templateCode),
      ]);

      if (!grouped[formItem.templateCode]) {
        grouped[formItem.templateCode] = {
          formName: formItem.formName,
          template: formTemplateData,
          reports: [],
        };
      }

      grouped[formItem.templateCode].reports.push({ report, formValues });
    }

    const workbook = new ExcelJS.Workbook();

    for (const { formName, template, reports } of Object.values(grouped)) {
      const formLabels = template?.formJson?.components || [];
      const formFields = formLabels.filter((item) => item.type !== "button");

      // Headers
      const dynamicHeaders = formFields.map((item) => item.label);
      const headers = [...STATIC_HEADERS, ...dynamicHeaders];

      // Add sheet
      const sheet = workbook.addWorksheet(safeSheetName(formName));

      // Freeze header row
      sheet.views = [{ state: "frozen", ySplit: 1 }];

      // Add header row
      const headerRow = sheet.addRow(headers);

      // Style header row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      for (const { report, formValues } of reports) {
        const baseRow = [
          report.taskDate ? getDateString(report.taskDate) : "--",
          report.startTime ? getTimeString(report.startTime) : "--",
          report?.endTime ? getTimeString(report.endTime) : "--",
          getTimeSpentFromTimestamps(report.startTime, report.endTime) || "0h 0m",
          report?.status === "Submitted" ? "Pending Approval" : report?.status,
          getFullName(
            report?.workforce?.givenName,
            report?.workforce?.familyName
          ) || "--",
          getFullName(
            report?.coordinator?.givenName,
            report?.coordinator?.familyName
          ) || "--",
          getFullName(
            report?.patient?.givenName,
            report?.patient?.familyName
          ) || "--",
          report?.patient?.medicalRecordNumber || "--",
          report?.createdTimestamp
            ? dateAndTime(report?.createdTimestamp)
            : "--",
        ];

        const answers = formFields.map((item) => {
          let answer = `${formValues[item.key] || ""}`.trim();
          if (item.type === "select" && Array.isArray(item.data?.values)) {
            const match = item.data.values.find(
              (v) => v.value === formValues[item.key]
            );
            answer = match ? match.label : "";
          }
          return answer;
        });

        const row = sheet.addRow([...baseRow, ...answers]);

        // Style data cells
        row.eachCell((cell) => {
          cell.alignment = {
            horizontal: "left",
            vertical: "middle",
            wrapText: true,
          };
        });
      }

      // Auto-fit column widths
      sheet.columns.forEach((col) => {
        let maxLength = 10;
        col.eachCell({ includeEmpty: true }, (cell) => {
          const value = cell.value ? cell.value.toString() : "";
          maxLength = Math.max(maxLength, value.length);
        });
        col.width = maxLength + 2;
      });
    }

    // Save file
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .slice(0, 19);

    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `Reports_${timestamp}.xlsx`);
  };

  const handleExportClick = async () => {
    setLoading(true);
    try {
      await exportExcel(formSummaryData?.tasksSummary);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SecondaryButton
        buttonText="Export CSV"
        loading={loading}
        handleOnClick={handleExportClick}
      />
    </>
  );
};

export default CsvExportButton;
